import type { TFunction } from 'i18next'
import type { RecurrenceEnd, RecurrenceRule, Weekday } from '../db/models'
import { parseDateKey } from '../date/calendar'

/** Montag, 5. Januar 2026 — ein bekannter Montag als Bezugspunkt für Wochentagsnamen. */
const A_MONDAY = new Date(2026, 0, 5)

/** „Donnerstag" / „Do" — aus `Intl`, keine Übersetzungsschlüssel für Wochentage. */
export function weekdayName(day: Weekday, locale: string, form: 'long' | 'short' = 'long'): string {
  const name = new Intl.DateTimeFormat(locale, { weekday: form })
    .format(new Date(A_MONDAY.getFullYear(), A_MONDAY.getMonth(), A_MONDAY.getDate() + day))
  return form === 'short' ? name.replace(/\.$/, '') : name // „Mo." → „Mo"
}

/** „Dezember" für 12. */
export function monthName(month: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(2026, month - 1, 1))
}

/** Schlüssel unter `recurrence.nth.*`: „1“ … „4“, „last“. */
export const nthKey = (nth: number) => (nth === -1 ? 'last' : String(nth))

const weekdayList = (days: readonly Weekday[], locale: string) =>
  new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' })
    .format([...new Set(days)].sort((a, b) => a - b).map(d => weekdayName(d, locale)))

const yearDate = (month: number, day: number, locale: string) =>
  locale.startsWith('de') ? `${day}. ${monthName(month, locale)}` : `${monthName(month, locale)} ${day}`

/**
 * Der Satz unter dem Wiederkehr-Wähler — **aus den Feldwerten erzeugt**, nie je Preset gespeichert.
 * (Things 3 zeigte bei „Jährlich" den Text von „Jeden Werktag"; genau das darf hier nicht
 * passieren.) Schlüssel unter `recurrence.summary.*`, Plural über `count`.
 */
export function describeRecurrence(rule: RecurrenceRule, t: TFunction, locale: string): string {
  const rhythm = describeRhythm(rule, t, locale)
  if (!rule.end || (rule.type === 'fixed' && rule.unit === 'week' && rule.weekdays.length === 0)) return rhythm
  return `${rhythm} ${describeEnd(rule.end, t, locale)}`
}

/** „Endet nach 3 Mal.“ / „Endet am 31.12.2026.“ — der zweite Satz, wenn die Wiederholung ein Ende hat. */
function describeEnd(end: RecurrenceEnd, t: TFunction, locale: string): string {
  if (end.kind === 'count') return t('recurrence.summary.endCount', { count: end.remaining })
  const date = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(parseDateKey(end.date))
  return t('recurrence.summary.endDate', { date })
}

function describeRhythm(rule: RecurrenceRule, t: TFunction, locale: string): string {
  switch (rule.type) {
    case 'quota':
      return t('recurrence.summary.quota', { count: rule.count, period: t(`recurrence.period.${rule.period}`) })
    case 'after_done':
      return t('recurrence.summary.afterDone', { span: t(`recurrence.span.${rule.unit}`, { count: rule.interval }) })
    case 'fixed':
      switch (rule.unit) {
        case 'day':
          return t('recurrence.summary.fixedDay', { count: rule.interval })
        case 'week':
          if (rule.weekdays.length === 0) return t('recurrence.summary.pickWeekday')
          return t('recurrence.summary.fixedWeek', { count: rule.interval, days: weekdayList(rule.weekdays, locale) })
        case 'month':
          if (rule.nthWeekday) {
            return t('recurrence.summary.fixedMonthNth', {
              count: rule.interval,
              nth: t(`recurrence.nth.${nthKey(rule.nthWeekday.nth)}`),
              weekday: weekdayName(rule.nthWeekday.weekday, locale),
            })
          }
          // 31 klemmt in jedem Monat auf den letzten Tag — so sagt es der Satz auch.
          if (rule.monthDay === 31) return t('recurrence.summary.fixedMonthLast', { count: rule.interval })
          return t('recurrence.summary.fixedMonth', { count: rule.interval, day: rule.monthDay })
        case 'year':
          return t('recurrence.summary.fixedYear', { count: rule.interval, date: yearDate(rule.yearMonth, rule.yearDay, locale) })
      }
  }
}
